/**
 * WS-ICON-01 — one icon per navigation LEAF, keyed by route.
 *
 * Christian, 2026-08-30: *"per leaf, no icon per section (take the admin page
 * as reference)"* — so this mirrors the console's treatment
 * (`apps/platform-admin/src/shell/NavIcons.tsx`): a small glyph beside every
 * leaf label, and nothing beside a section caption.
 *
 * WHERE THE GLYPHS COME FROM. `@aeriosiq/ui/icons` — the Lucide layer (D80,
 * 2026-08-12). Not a per-app file, and not `@mui/icons-material`, which D80
 * replaced because Material icons are filled paths that no CSS can unfill.
 * `/admin/audit-log` is the one route that also exists in the console, so it
 * deliberately draws the SAME glyph the console does rather than a lookalike.
 * That one shared import is the whole reason the story asked for a shared set.
 *
 * WHY KEYED BY ROUTE AND NOT BY LABEL. Two workspaces both show "Data
 * Acquisitions", pointing at the same route; they should carry the same icon,
 * and keying by route gets that for free. It also means a renamed label keeps
 * its glyph.
 *
 * WHY A PLAIN MAP AND NOT AN EXHAUSTIVE RECORD. The console's map is
 * `Record<AdminNavPath, …>` because its six routes are a closed union. This
 * app's routes are not — `WorkspaceNavItem.to` is `string | undefined`, and
 * some leaves are placeholders with no route at all. So a missing entry is a
 * legitimate state here, handled by rendering the same-sized spacer (AC-4)
 * rather than by failing the build.
 */
import {
  BellIcon,
  BookOpenIcon,
  BoxIcon,
  BugIcon,
  Building2Icon,
  CameraIcon,
  ChartLineIcon,
  ClipboardListIcon,
  FileTextIcon,
  FileUpIcon,
  FolderIcon,
  HistoryIcon,
  HouseIcon,
  InboxIcon,
  LayoutGridIcon,
  LibraryIcon,
  MapPinIcon,
  NotebookPenIcon,
  ReceiptTextIcon,
  ScanSearchIcon,
  ScrollTextIcon,
  SettingsIcon,
  ShieldIcon,
  SlidersHorizontalIcon,
  TimerIcon,
  UserIcon,
  UsersIcon,
  WindIcon,
  WrenchIcon,
} from "@aeriosiq/ui/icons";
// The ONE overlap with the platform-admin console. Same route, same glyph —
// drawn from the shared set rather than re-picked, so the two chromes cannot
// disagree about what an audit log looks like.
import { AuditLogIcon } from "@aeriosiq/ui";

import type { IconComponent } from "./types.js";

export const NAV_LEAF_ICONS: Readonly<Record<string, IconComponent>> = {
  // ── Field Data ──────────────────────────────────────────────────────────
  "/field-data/upload": FileUpIcon,
  "/field-data/intake": InboxIcon,
  "/field-data/intake/history": HistoryIcon,
  "/field-data/tasks": ClipboardListIcon,
  "/field-data/standby": TimerIcon,

  // ── Projects ────────────────────────────────────────────────────────────
  "/": HouseIcon,
  "/projects": FolderIcon,
  "/contracts": FileTextIcon,
  "/contract-positions": NotebookPenIcon,
  "/inspection-instructions": ClipboardListIcon,
  "/data-acquisitions": CameraIcon,
  "/reports": ScrollTextIcon,

  // ── Analysis ────────────────────────────────────────────────────────────
  "/defect-catalogues": BookOpenIcon,
  "/statistics": ChartLineIcon,

  // ── Assets ──────────────────────────────────────────────────────────────
  "/sites": MapPinIcon,
  "/turbines": WindIcon,
  "/turbine-models": BoxIcon,
  "/components": WrenchIcon,
  "/component-models": LayoutGridIcon,

  // ── Administration ──────────────────────────────────────────────────────
  "/admin/users": UsersIcon,
  "/admin/contacts": UserIcon,
  "/admin/partner-companies": Building2Icon,
  "/admin/audit-log": AuditLogIcon,
  "/admin/notification-rules": BellIcon,
  "/settings/limits": SlidersHorizontalIcon,
  "/settings/billing": ReceiptTextIcon,
  "/account/defect-collections": LibraryIcon,
  "/ai-models": ScanSearchIcon,
  "/ai-findings-import": FileUpIcon,
  "/auto-group-rules": SettingsIcon,

  // ── Platform ────────────────────────────────────────────────────────────
  "/platform-admin/users": ShieldIcon,
  "/platform-admin/bug-reports": BugIcon,
};

/** The glyph for a leaf, or `undefined` when the route has none mapped. */
export function navLeafIcon(to: string | undefined): IconComponent | undefined {
  return to === undefined ? undefined : NAV_LEAF_ICONS[to];
}
